/**
 * PGLP | MONTE HERMOM
 * COMPONENTE: ImovelParadigma_Identificacao
 * VERSÃO: 2.1.0
 * ------------------------------------------------------------
 * NOVIDADES v2.1.0:
 *   ✔ FIX: Status/badge/pendências agora são ESCOPADOS por fase
 *   ✔ Não usa mais validacao.status/validacao.badge (global)
 *   ✔ Usa validacao.por_fase.IDENTIFICACAO (apenas esta fase)
 *   ✔ Pendências de outras fases não afetam mais o status visual
 *
 * v2.0.0:
 *   ✔ Adiciona campos RGI / Matrícula e Código Cartográfico
 *   ✔ Checklist dinâmico via useValidacaoImovel (fase IDENTIFICACAO)
 *   ✔ Botão Avançar SEMPRE habilitado (decisão do usuário)
 *   ✔ Persiste em formData.rgi e formData.cartografico
 * ============================================================
 */

import React, { useMemo, useState } from 'react';
import {
  Fingerprint, AlertOctagon, CheckCircle2, ArrowLeft, ArrowRight,
  Calculator, FileSearch, Ruler, TrendingUp, ScrollText, MapPin,
  Hash, AlertTriangle, X,
} from 'lucide-react';
import useValidacaoImovel from '../../hooks/useValidacaoImovel';

/* ============================================================
   MÁSCARAS
============================================================ */

function aplicarMascaraNumero(valor) {
  const numericValue = String(valor || '').replace(/\D/g, '');
  if (!numericValue) return '';
  return (Number(numericValue) / 100).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function normalizarRGI(valor) {
  return String(valor || '')
    .toUpperCase()
    .replace(/[^A-Z0-9.\-/\s]/g, '')
    .slice(0, 40);
}

function normalizarCartografico(valor) {
  return String(valor || '')
    .toUpperCase()
    .replace(/[^A-Z0-9.\-/]/g, '')
    .slice(0, 30);
}

/* ============================================================
   LABELS DA FASE
============================================================ */

const LABELS_CAMPOS = {
  areaTerreno:  'Área do terreno',
  frente:       'Frente principal',
  rgi:          'RGI / Matrícula',
  cartografico: 'Código cartográfico',
};

function labelDoCampo(c) {
  return LABELS_CAMPOS[c] || c;
}

/* ============================================================
   COMPONENTE PRINCIPAL
============================================================ */

export default function ImovelParadigma_Identificacao({
  formData,
  setFormData,
  onBack,
  onNext,
  imovelUuid = null,
  codigoLaudo = null,
}) {
  const [showJustificativa, setShowJustificativa] = useState(
    Boolean(formData?.justificativaDivergencia)
  );

  /* ---------- Validação real (hook) ---------- */

  const validacao = useValidacaoImovel(formData, {
    imovelUuid:  imovelUuid || formData?.id || null,
    codigoLaudo: codigoLaudo || formData?.laudo_id || formData?.laudoRef || null,
    autoValidar: false,
  });

  /* ============================================================
     FASE ATUAL — ESCOPO LOCAL (não olha outras fases)
  ============================================================ */

  const faseAtual = validacao.por_fase?.IDENTIFICACAO;
  const pendenciasFase = faseAtual?.pendencias || [];
  const conforme = pendenciasFase.length === 0;

  // Badge calculado APENAS com base nesta fase
  const badgeFase = conforme
    ? {
        label: 'Conforme',
        classe: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400',
      }
    : {
        label: `Não conforme · ${pendenciasFase.length}`,
        classe: 'bg-rose-500/10 border-rose-500/30 text-rose-400',
      };

  /* ---------- Checklist local da fase ---------- */

  const checklist = useMemo(() => {
    if (!faseAtual) return [];

    const camposFase = ['areaTerreno', 'frente', 'rgi', 'cartografico'];

    return camposFase.map((campo) => {
      const pendencia = pendenciasFase.find((p) => p.campo === campo);
      return {
        campo,
        label: labelDoCampo(campo),
        motivo: pendencia?.motivo || null,
        ok: !pendencia,
      };
    });
  }, [faseAtual, pendenciasFase]);

  /* ---------- Progresso ---------- */

  const progresso = useMemo(() => {
    const campos = [
      formData?.areaTerreno,
      formData?.frente,
      formData?.rgi,
      formData?.cartografico,
    ];
    const preenchidos = campos.filter((v) => String(v || '').trim() !== '').length;
    return Math.round((preenchidos / campos.length) * 100);
  }, [formData]);

  /* ---------- Ações ---------- */

  const concluir = () => {
    onNext?.();
  };

  const inputBase =
    'w-full h-12 bg-slate-950 border border-white/10 rounded-xl px-4 font-mono font-black text-lg text-white outline-none focus:border-amber-400 transition-all text-right placeholder:text-slate-700';
  const inputTexto =
    'w-full h-12 bg-slate-950 border border-white/10 rounded-xl px-4 font-bold text-base text-white outline-none focus:border-amber-400 transition-all placeholder:text-slate-700';

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 w-full space-y-4">

      {/* HEADER */}
      <div className="bg-slate-900 rounded-2xl px-5 py-3.5 border border-white/5 shadow-lg flex flex-col lg:flex-row justify-between gap-3 lg:items-center">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 bg-slate-950 rounded-xl flex items-center justify-center border border-white/10 shrink-0">
            <Fingerprint className="text-amber-400" size={16} />
          </div>
          <div className="min-w-0">
            <h2 className="text-base font-black italic tracking-tight text-white uppercase leading-none">
              Identificação do Objeto
            </h2>
            <p className="text-[8px] text-slate-500 font-bold uppercase tracking-widest italic mt-1.5">
              Dimensões físicas + Identificação registral · NBR 14653
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          {/* Badge ESCOPADO à fase atual */}
          <div className={`px-4 py-2 rounded-full border text-[9px] font-black uppercase tracking-widest ${badgeFase.classe}`}>
            {badgeFase.label}
          </div>
          <div className="flex items-center gap-3">
            <div className="w-32 h-1.5 bg-slate-950 rounded-full overflow-hidden border border-white/5">
              <div
                className="h-full bg-amber-400 transition-all duration-500"
                style={{ width: `${progresso}%` }}
              />
            </div>
            <span className="text-[9px] font-black text-amber-400 uppercase tracking-widest">
              {progresso}%
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4">

        {/* COLUNA ESQUERDA: FORMULÁRIO */}
        <div className="col-span-12 lg:col-span-8 space-y-4">

          {/* BLOCO 1: DIMENSÕES FÍSICAS */}
          <div className="bg-slate-900 p-5 rounded-2xl border border-white/5 shadow-xl space-y-4">
            <div className="flex items-center gap-2 pb-2.5 border-b border-white/5">
              <Calculator className="text-amber-400" size={14} />
              <h3 className="text-[11px] font-black text-white uppercase italic tracking-tight">
                Dimensões Físicas
              </h3>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <label className="block space-y-2">
                <span className="text-[8px] font-black uppercase text-amber-400 ml-2 tracking-widest flex items-center gap-1">
                  <Ruler size={10} /> Área do Terreno (m²) *
                </span>
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="0,00"
                  className={inputBase}
                  value={formData?.areaTerreno || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      areaTerreno: aplicarMascaraNumero(e.target.value),
                    }))
                  }
                />
              </label>

              <label className="block space-y-2">
                <span className="text-[8px] font-black uppercase text-amber-400 ml-2 tracking-widest flex items-center gap-1">
                  <Ruler size={10} /> Frente Principal (m) *
                </span>
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="0,00"
                  className={inputBase}
                  value={formData?.frente || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      frente: aplicarMascaraNumero(e.target.value),
                    }))
                  }
                />
              </label>
            </div>
          </div>

          {/* BLOCO 2: IDENTIFICAÇÃO REGISTRAL */}
          <div className="bg-slate-900 p-5 rounded-2xl border border-white/5 shadow-xl space-y-4">
            <div className="flex items-center gap-2 pb-2.5 border-b border-white/5">
              <ScrollText className="text-amber-400" size={14} />
              <h3 className="text-[11px] font-black text-white uppercase italic tracking-tight">
                Identificação Registral
              </h3>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <label className="block space-y-2">
                <span className="text-[8px] font-black uppercase text-amber-400 ml-2 tracking-widest flex items-center gap-1">
                  <ScrollText size={10} /> RGI / Matrícula *
                </span>
                <input
                  type="text"
                  placeholder="Ex: 12.345 ou 12.345 - 6º CRI"
                  className={inputTexto}
                  value={formData?.rgi || formData?.matricula || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      rgi: normalizarRGI(e.target.value),
                    }))
                  }
                />
                <p className="text-[7px] text-slate-500 font-bold uppercase tracking-widest ml-2 italic">
                  Número de registro do imóvel no cartório (matrícula)
                </p>
              </label>

              <label className="block space-y-2">
                <span className="text-[8px] font-black uppercase text-amber-400 ml-2 tracking-widest flex items-center gap-1">
                  <Hash size={10} /> Código Cartográfico *
                </span>
                <input
                  type="text"
                  placeholder="Ex: 04.123.001.0001"
                  className={inputTexto}
                  value={formData?.cartografico || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      cartografico: normalizarCartografico(e.target.value),
                    }))
                  }
                />
                <p className="text-[7px] text-slate-500 font-bold uppercase tracking-widest ml-2 italic">
                  Código do imóvel na prefeitura (cadastro municipal)
                </p>
              </label>
            </div>

            {(formData?.rgi || formData?.cartografico) && (
              <div className="bg-slate-950 p-3 rounded-xl border border-white/5 flex items-center gap-3">
                <MapPin size={14} className="text-amber-400 shrink-0" />
                <p className="text-[8px] font-black uppercase text-slate-400 tracking-widest leading-relaxed">
                  Estes dados serão usados para confronto automático com a matrícula
                  anexada em DOCUMENTOS. Divergências gerarão tarefa automática.
                </p>
              </div>
            )}
          </div>

          {/* BLOCO 3: DIVERGÊNCIA DE ÁREA */}
          <div
            className={`p-4 rounded-2xl transition-all duration-500 border ${
              showJustificativa
                ? 'bg-amber-400/5 border-amber-400/30'
                : 'bg-slate-900 border-white/5'
            }`}
          >
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <AlertOctagon
                  size={16}
                  className={showJustificativa ? 'text-amber-400' : 'text-slate-600'}
                />
                <div>
                  <p className="text-[10px] font-black uppercase text-white tracking-widest">
                    Divergência de Área
                  </p>
                  <p className="text-[8px] text-slate-500 font-bold uppercase tracking-widest mt-0.5">
                    IPTU × Matrícula (opcional)
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowJustificativa((v) => !v)}
                className={`text-[9px] font-black uppercase px-4 py-2 rounded-lg border transition-all ${
                  showJustificativa
                    ? 'bg-amber-400 text-slate-950 border-amber-400'
                    : 'bg-white/5 text-slate-400 border-white/10 hover:bg-white/10'
                }`}
              >
                {showJustificativa ? 'Remover' : 'Informar'}
              </button>
            </div>

            {showJustificativa && (
              <textarea
                className="mt-3 w-full bg-slate-950 border border-white/10 rounded-xl p-3 font-bold text-white text-[11px] outline-none focus:border-amber-400 transition-all placeholder:text-slate-700 placeholder:font-bold leading-relaxed"
                placeholder="Descreva a divergência técnica entre os documentos oficiais e a realidade física..."
                rows={3}
                value={formData?.justificativaDivergencia || ''}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    justificativaDivergencia: e.target.value,
                  }))
                }
              />
            )}
          </div>
        </div>

        {/* COLUNA DIREITA: VALIDAÇÃO NORMATIVA (ESCOPO DA FASE) */}
        <div className="col-span-12 lg:col-span-4">
          <div className="bg-slate-900 p-5 rounded-2xl border border-white/5 shadow-xl flex flex-col h-full">
            <div className="flex items-center gap-2 pb-3 border-b border-white/5 mb-4">
              <FileSearch className="text-amber-400" size={14} />
              <h3 className="text-[11px] font-black text-white uppercase italic tracking-tight">
                Validação Normativa
              </h3>
            </div>

            {/* CHECKLIST DINÂMICO — APENAS CAMPOS DESTA FASE */}
            <div className="space-y-2 mb-4">
              {checklist.map((item) => (
                <div
                  key={item.campo}
                  className={`flex items-center gap-2 p-2.5 rounded-lg border ${
                    item.ok
                      ? 'bg-emerald-500/10 border-emerald-500/20'
                      : 'bg-slate-950 border-white/5'
                  }`}
                >
                  {item.ok ? (
                    <CheckCircle2 size={12} className="text-emerald-500 shrink-0" />
                  ) : (
                    <AlertTriangle size={12} className="text-amber-400 shrink-0" />
                  )}
                  <span
                    className={`text-[9px] font-black uppercase tracking-widest ${
                      item.ok ? 'text-emerald-400' : 'text-amber-400'
                    }`}
                  >
                    {item.label}
                  </span>
                </div>
              ))}
            </div>

            {/* RESUMO — ESCOPADO À FASE */}
            <div className="bg-slate-950 border border-white/5 rounded-xl p-3 mb-4">
              <p className="text-[9px] text-slate-500 font-bold leading-relaxed">
                {conforme
                  ? 'Todos os campos desta fase estão preenchidos conforme o validador.'
                  : `${pendenciasFase.length} pendência(s) nesta fase. As tarefas foram criadas automaticamente.`}
              </p>
            </div>

            {/* AVISO QUANDO NÃO CONFORME (SOMENTE SE A FASE ESTIVER INCOMPLETA) */}
            {!conforme && (
              <div className="p-2.5 bg-rose-500/10 border border-rose-500/20 rounded-lg flex items-center gap-2 mb-4">
                <AlertOctagon size={11} className="text-rose-400 shrink-0" />
                <p className="text-[8px] font-black text-rose-400 uppercase tracking-widest leading-tight">
                  Você pode avançar mesmo assim. As tarefas estão na aba Tarefas.
                </p>
              </div>
            )}

            {/* BOTÕES */}
            <div className="space-y-2 mt-auto">
              <button
                type="button"
                onClick={concluir}
                className="w-full py-3.5 rounded-xl bg-amber-400 text-slate-950 font-black text-[10px] uppercase tracking-[0.2em] hover:bg-white transition-all flex items-center justify-center gap-2"
              >
                Confirmar e Avançar
                <ArrowRight size={14} />
              </button>

              <button
                type="button"
                onClick={onBack}
                className="w-full py-3 rounded-xl bg-white/5 border border-white/10 text-white font-black text-[10px] uppercase tracking-[0.2em] hover:bg-white/10 transition-all flex items-center justify-center gap-2"
              >
                <ArrowLeft size={14} />
                Voltar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* RODAPÉ */}
      <div className="flex items-center justify-center gap-3 pt-2">
        <TrendingUp size={12} className="text-amber-400/40" />
        <p className="text-[8px] font-black text-slate-700 uppercase tracking-[0.4em]">
          Dados auditados · Marcus Ferraz MH-2026
        </p>
      </div>
    </div>
  );
}